export type OuterLoopStockerOption = {
  id: string;
  name: string;
  processTypes: readonly string[];
  outside: boolean;
};

function normalizedProcessTypes(processTypes: readonly string[]): string[] {
  return [
    ...new Set(
      processTypes.map((processType) => String(processType).trim().toUpperCase()).filter(Boolean)
    ),
  ];
}

/**
 * Preserve the legacy automatic path: outside stockers first, followed by one
 * stocker for each process type in process-flow order.
 */
export function defaultOuterLoopStockerIds(
  stockers: readonly OuterLoopStockerOption[],
  processSequence: readonly string[]
): string[] {
  const outsideIds = stockers.filter((stocker) => stocker.outside).map((stocker) => stocker.id);
  const bayStockers = stockers.filter((stocker) => !stocker.outside);
  const availableTypes = [
    ...new Set(bayStockers.flatMap((stocker) => normalizedProcessTypes(stocker.processTypes))),
  ];
  const availableSet = new Set(availableTypes);
  const configuredTypes = processSequence
    .map((processType) => String(processType).trim().toUpperCase())
    .filter(
      (processType, index, all) =>
        availableSet.has(processType) && all.indexOf(processType) === index
    );
  const orderedTypes = [
    ...configuredTypes,
    ...availableTypes.filter((processType) => !configuredTypes.includes(processType)),
  ];
  const selectedBayIds = [
    ...new Set(
      orderedTypes.flatMap((processType) => {
        const stocker = bayStockers.find((candidate) =>
          normalizedProcessTypes(candidate.processTypes).includes(processType)
        );
        return stocker ? [stocker.id] : [];
      })
    ),
  ];

  return [...outsideIds, ...selectedBayIds.filter((id) => !outsideIds.includes(id))];
}

/** Unique stocker ids in process-flow step order (first stocker per step). */
export function outerLoopStockerIdsFromProcessSteps(
  steps: readonly { stockerIds?: string[] | null }[]
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const step of steps) {
    const id = String(step.stockerIds?.[0] ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Outer-loop visit order is shared by every outer OHT and comes from process
 * flow. Per-OHT saved lists are ignored. Layouts without step stockers keep
 * the automatic outside-then-bay path.
 */
export function resolveOuterLoopStockerIds(
  stockers: readonly OuterLoopStockerOption[],
  processSequence: readonly string[],
  processFlowSteps?: readonly { stockerIds?: string[] | null }[]
): string[] {
  const availableIds = new Set(stockers.map((stocker) => stocker.id));
  const fromFlow = outerLoopStockerIdsFromProcessSteps(processFlowSteps ?? []).filter((id) =>
    availableIds.has(id)
  );
  if (fromFlow.length > 0) return fromFlow;
  return defaultOuterLoopStockerIds(stockers, processSequence);
}
